import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { HoldsOverlay } from './HoldsOverlay';
import { RouteLineOverlay } from './RouteLineOverlay';
import { cropImageStyle, cropViewBox, focusCrop, padRect, startToTopBounds } from './hold-focus';
import { isDrawableLine } from './route-line';
import { sendClimbUrl } from '../../utils/climb-url';
import type { SendClimb } from '../../types/send';

/** The tile is square, so its crop is squared up to match. */
const TILE_ASPECT = 1;

/**
 * A little room around the start and the top, as a fraction of the box they
 * span: a ring flush with the tile's edge reads as cut off, even when it is not.
 */
const ENDS_PADDING = 0.06;

/** Keeps white text legible over whatever the photo happens to be there. */
const TEXT_SHADOW = { textShadow: '0 1px 3px rgba(0,0,0,0.9)' };

interface ClimbTileProps {
  climb: SendClimb;
  /** What sits in the top right corner, e.g. how a send went. */
  corner?: ReactNode;
  /**
   * The climb was deleted: the tile is greyed out, with this label across it.
   * It still opens the climb's page, which answers for a deleted climb too.
   */
  discardedLabel?: string;
  /** History state the climb's page is opened with (see profile-origin). */
  linkState?: unknown;
  /** Names the tile and its parts for the tests: `<testId>-image`, `-grade`, … */
  testId?: string;
}

/**
 * One climb of a profile's grid: the climb's photo, zoomed to the largest
 * square from its start holds to its top with every circle drawn on, and over
 * it what a glance wants — the grade (top left), the `corner` (top right), and
 * where (the bottom edge).
 *
 * A route drawn as a line is shown whole rather than cropped: its line runs
 * the height of the wall, and any crop of it throws away the shape that
 * identifies it. So is a climb whose holds were never marked. A board problem
 * has no photo of its own; the wall's stands in, without its holds — those are
 * grid cells, drawn by the board page and not by a thumbnail.
 */
export function ClimbTile({ climb, corner, discardedLabel, linkState, testId = 'climb-tile' }: ClimbTileProps) {
  // Natural pixel shape of the photo, read off the <img> once it loads: the
  // crop is squared up in pixels, and a box that is square in fractions is only
  // square on screen if the photo itself is.
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);

  const isBoard = climb.climb_type === 'BoardClimb';
  const imageUrl = (isBoard ? climb.board?.image_url : climb.image_medium_url) ?? null;
  const holds = isBoard ? null : (climb.holds ?? null);
  const drawnAsLine = !isBoard && isDrawableLine(climb.line);

  const bounds = useMemo(() => (drawnAsLine ? null : startToTopBounds(holds)), [drawnAsLine, holds]);
  const crop = useMemo(
    () =>
      bounds && natural
        ? focusCrop(padRect(bounds, ENDS_PADDING), natural.width, natural.height, TILE_ASPECT)
        : null,
    [bounds, natural]
  );

  const title = climb.name;
  const discarded = discardedLabel !== undefined;
  const url = sendClimbUrl(climb);
  // The gym, or for a global board problem — which belongs to no gym — the
  // board itself.
  const place = climb.location?.name ?? climb.board?.name ?? null;
  const logo = climb.location?.logo_small_url ?? null;

  const body = (
    <>
      {imageUrl ? (
        <>
          <img
            data-testid={`${testId}-image`}
            src={imageUrl}
            alt={title}
            className={crop ? 'absolute max-w-none object-cover' : 'absolute inset-0 h-full w-full object-cover'}
            // Blow the photo up so the crop alone fills the tile, then slide it
            // into place (see cropImageStyle).
            style={cropImageStyle(crop)}
            loading="lazy"
            decoding="async"
            draggable={false}
            onLoad={(e) => {
              const { naturalWidth, naturalHeight } = e.currentTarget;
              if (naturalWidth && naturalHeight) setNatural({ width: naturalWidth, height: naturalHeight });
            }}
          />
          {/* The same marks the climb page draws, still: a grid of tiles is
              no place for thirty circles sweeping themselves in at once. */}
          <HoldsOverlay
            holds={holds}
            naturalWidth={natural?.width ?? null}
            naturalHeight={natural?.height ?? null}
            viewBox={cropViewBox(crop, natural)}
            objectFit="cover"
            mode="all"
            circuit={!!climb.circuit}
            animate={false}
          />
          {drawnAsLine && (
            <RouteLineOverlay
              line={climb.line}
              naturalWidth={natural?.width ?? null}
              naturalHeight={natural?.height ?? null}
              objectFit="cover"
              // At this size the full-size stroke resolves to a fraction of a pixel.
              marks="compact"
              animate={false}
            />
          )}
        </>
      ) : (
        <span
          data-testid={`${testId}-placeholder`}
          className="absolute inset-0 flex items-center justify-center p-2 text-center text-xs text-muted-foreground"
        >
          {title}
        </span>
      )}

      {/* A scrim along the top and bottom edges, so the marks read over a
          photo of any brightness; the middle — the holds — is left alone. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-black/60 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/70 to-transparent" />

      {climb.difficulty && (
        <span
          data-testid={`${testId}-grade`}
          className="absolute left-1.5 top-1.5 text-xs font-semibold leading-none text-white"
          style={TEXT_SHADOW}
        >
          {climb.difficulty}
        </span>
      )}

      {corner && <span className="absolute right-1 top-1 rounded-full bg-black/50 p-0.5">{corner}</span>}

      {discarded && (
        <span
          data-testid={`${testId}-discarded`}
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 bg-black/60 py-0.5 text-center text-[11px] font-medium text-white"
        >
          {discardedLabel}
        </span>
      )}

      {place && (
        <span data-testid={`${testId}-place`} className="absolute inset-x-0 bottom-0 flex items-center gap-1 px-1.5 pb-1.5">
          {logo ? (
            <img src={logo} alt="" className="h-4 w-4 shrink-0 rounded-full object-cover" />
          ) : (
            <span
              aria-hidden
              className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white/90 text-[9px] font-bold leading-none text-black"
            >
              {place.charAt(0).toUpperCase()}
            </span>
          )}
          <span className="truncate text-[11px] font-medium leading-tight text-white" style={TEXT_SHADOW}>
            {place}
          </span>
        </span>
      )}
    </>
  );

  // A deleted climb is still the climber's work, so it keeps its place in the
  // grid and its page, drained of colour.
  const className = `relative block aspect-square overflow-hidden bg-muted${discarded ? ' opacity-60 grayscale' : ''}`;

  return url ? (
    <Link to={url} state={linkState} aria-label={title} className={className} data-testid={testId}>
      {body}
    </Link>
  ) : (
    <div aria-label={title} className={className} data-testid={testId}>
      {body}
    </div>
  );
}
